import React, { useState, useEffect, useRef } from 'react';
import { ref, onValue, query, orderByChild, equalTo, push, set, update } from 'firebase/database';
import { auth, db } from './firebase';

export default function ChatTab({ pharmacyId, pharmacyData }) {
  const [chats, setChats]           = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages]     = useState([]);
  const [input, setInput]           = useState('');
  const [sending, setSending]       = useState(false);
  const [loading, setLoading]       = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [search, setSearch]         = useState('');
  const messagesEndRef              = useRef(null);

  // ── LOAD ALL CHATS FOR THIS PHARMACY ──────────────────────────────────────
  // Queried server-side by pharmacyId (chats always carry one — see
  // ChatScreen.js) instead of pulling every pharmacy's chats to filter
  // client-side. The pharmacyName check stays as a belt-and-braces filter
  // in case a chat somehow only carries the display name.
  useEffect(() => {
    if (!pharmacyId) return;
    const unsubscribe = onValue(query(ref(db, 'chats'), orderByChild('pharmacyId'), equalTo(pharmacyId)), snap => {
      if (snap.exists()) {
        const all = Object.entries(snap.val())
          .map(([k, v]) => ({ id: k, ...v }))
          .filter(c =>
            c.pharmacyId === pharmacyId ||
            c.pharmacyName === pharmacyData?.pharmacyName
          )
          .sort((a, b) =>
            new Date(b.updatedAt || b.lastMessageAt || 0) -
            new Date(a.updatedAt || a.lastMessageAt || 0)
          );
        setChats(all);
      } else { setChats([]); }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [pharmacyId, pharmacyData?.pharmacyName]);

  // ── LOAD MESSAGES FOR ACTIVE CHAT ─────────────────────────────────────────
  useEffect(() => {
    if (!activeChat) return;
    setLoadingMsgs(true);
    setMessages([]);

    // ✅ FIX: use chats/${activeChat.id}/messages
    const unsubscribe = onValue(ref(db, `chats/${activeChat.id}/messages`), snap => {
      if (snap.exists()) {
        const data = Object.entries(snap.val())
          .map(([k, v]) => ({ id: k, ...v }))
          .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        setMessages(data);

        // Mark customer messages as read by pharmacy
        data.forEach(msg => {
          if (msg.senderRole === 'customer' && !msg.readByPharmacy) {
            update(ref(db, `chats/${activeChat.id}/messages/${msg.id}`), {
              readByPharmacy: true,
            });
          }
        });

        // Clear unread flag on chat metadata
        update(ref(db, `chats/${activeChat.id}`), {
          unreadByPharmacy: false,
          lastSeen_pharmacy: new Date().toISOString(),
        });
      } else { setMessages([]); }
      setLoadingMsgs(false);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    });
    return () => unsubscribe();
  }, [activeChat]);

  // ── SEND MESSAGE ──────────────────────────────────────────────────────────
  const sendMessage = async () => {
    if (!input.trim() || !activeChat) return;
    setSending(true);
    const text = input.trim();
    setInput('');
    try {
      // ✅ FIX: write to chats/${activeChat.id}/messages
      const msgRef = push(ref(db, `chats/${activeChat.id}/messages`));
      await set(msgRef, {
        text,
        senderId:       auth.currentUser?.uid,
        senderName:     pharmacyData?.pharmacyName || 'Pharmacist',
        senderRole:     'pharmacy',
        createdAt:      new Date().toISOString(),
        readByPharmacy: true,
        readByCustomer: false,
      });

      // ✅ FIX: update metadata at chats/${activeChat.id}
      await update(ref(db, `chats/${activeChat.id}`), {
        lastMessage:    text,
        lastMessageAt:  new Date().toISOString(),
        lastMessageBy:  'pharmacy',
        unreadByCustomer: true,
        updatedAt:      new Date().toISOString(),
      });

      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (e) {
      alert('Error sending message. Please try again.');
      setInput(text);
    } finally { setSending(false); }
  };

  const QUICK_REPLIES = [
    'Your order is being prepared now. ✅',
    'We have that medicine in stock. 💊',
    'Sorry, that medicine is currently out of stock.',
    'Your order will be ready in about 15 minutes.',
    'Please upload a valid prescription for this medicine.',
    'We recommend taking this medicine with food.',
    'Please consult your doctor before switching medicines.',
    'Your order has been dispatched to the rider. 🛵',
  ];

  const formatTime = (d) =>
    new Date(d).toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit' });

  const formatDate = (d) => {
    const date      = new Date(d);
    const today     = new Date();
    const yesterday = new Date(today - 86400000);
    if (date.toDateString() === today.toDateString())     return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-GH', { month: 'short', day: 'numeric' });
  };

  const filteredChats = chats.filter(c =>
    !search ||
    c.customerName?.toLowerCase().includes(search.toLowerCase()) ||
    c.orderId?.toLowerCase().includes(search.toLowerCase())
  );

  const totalUnread = chats.filter(c => c.unreadByPharmacy).length;

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 130px)', gap: 0, borderRadius: 12, overflow: 'hidden', border: '1px solid #E5E7EB' }}>

      {/* ── LEFT — CHAT LIST ─────────────────────────────────────────────── */}
      <div style={{ width: 320, backgroundColor: '#FFF', borderRight: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>

        {/* LIST HEADER */}
        <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid #F3F4F6' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <h3 style={{ fontWeight: 700, margin: 0, fontSize: 16 }}>💬 Customer Chats</h3>
            {totalUnread > 0 && (
              <span style={{ backgroundColor: '#EF4444', color: '#FFF', borderRadius: 20, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>
                {totalUnread} new
              </span>
            )}
          </div>
          <input
            style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: '1px solid #E5E7EB', fontSize: 13, backgroundColor: '#F7F8FA', outline: 'none', boxSizing: 'border-box' }}
            placeholder="Search by customer or order..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {/* CHAT LIST */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#888', fontSize: 13 }}>
              Loading chats...
            </div>
          ) : filteredChats.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <p style={{ fontSize: 36, margin: '0 0 10px' }}>💬</p>
              <p style={{ fontWeight: 600, color: '#333', marginBottom: 4, fontSize: 14 }}>No chats yet</p>
              <p style={{ fontSize: 12, color: '#888', lineHeight: 1.5 }}>
                Customers will message you from their order tracking screen
              </p>
            </div>
          ) : (
            filteredChats.map(chat => (
              <div
                key={chat.id}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  borderBottom: '0.5px solid #F3F4F6',
                  backgroundColor:
                    activeChat?.id === chat.id ? '#E8F8F2' :
                    chat.unreadByPharmacy ? '#FFFBEB' : '#FFF',
                  borderLeft:
                    activeChat?.id === chat.id ? '3px solid #1D9E75' :
                    chat.unreadByPharmacy ? '3px solid #F59E0B' : '3px solid transparent',
                  transition: 'background 0.1s',
                }}
                onClick={() => { setActiveChat(chat); setMessages([]); }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: '#E8F8F2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                      👤
                    </div>
                    <div>
                      <p style={{ fontWeight: 700, fontSize: 14, margin: 0, color: '#1A1A1A' }}>
                        {chat.customerName || 'Customer'}
                      </p>
                      {chat.orderId && (
                        <p style={{ fontSize: 11, color: '#1D9E75', margin: '1px 0 0', fontWeight: 600 }}>
                          Order #{chat.orderId.slice(-6).toUpperCase()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <p style={{ fontSize: 11, color: '#888', margin: 0 }}>
                      {chat.lastMessageAt ? formatTime(chat.lastMessageAt) : ''}
                    </p>
                    {chat.unreadByPharmacy && (
                      <span style={{ backgroundColor: '#F59E0B', color: '#FFF', borderRadius: 20, padding: '1px 6px', fontSize: 10, fontWeight: 700, marginTop: 2, display: 'inline-block' }}>
                        New
                      </span>
                    )}
                  </div>
                </div>
                {chat.lastMessage && (
                  <p style={{ fontSize: 12, color: '#888', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingLeft: 44 }}>
                    {chat.lastMessageBy === 'pharmacy' ? '✓ You: ' : ''}{chat.lastMessage}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── RIGHT — MESSAGES ─────────────────────────────────────────────── */}
      {activeChat ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#F7F8FA' }}>

          {/* CHAT HEADER */}
          <div style={{ backgroundColor: '#1D9E75', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
              👤
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontWeight: 700, fontSize: 15, color: '#FFF', margin: 0 }}>
                {activeChat.customerName || 'Customer'}
              </p>
              <p style={{ fontSize: 12, color: '#A8EDCF', margin: 0 }}>
                {activeChat.orderId ? `Order #${activeChat.orderId.slice(-6).toUpperCase()} · ` : ''}
                {activeChat.customerEmail}
              </p>
            </div>
            <div style={{ backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '4px 10px' }}>
              <p style={{ fontSize: 11, color: '#A8EDCF', margin: 0 }}>
                {activeChat.pharmacyName || pharmacyData?.pharmacyName}
              </p>
            </div>
          </div>

          {/* MESSAGES AREA */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
            {loadingMsgs ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>Loading messages...</div>
            ) : messages.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <p style={{ fontSize: 36, marginBottom: 10 }}>💬</p>
                <p style={{ fontWeight: 600, color: '#333', marginBottom: 4 }}>Start the conversation</p>
                <p style={{ fontSize: 13, color: '#888' }}>
                  Reply to {activeChat.customerName || 'the customer'} about their order
                </p>
              </div>
            ) : (
              <>
                {(() => {
                  let lastDate = null;
                  return messages.map(msg => {
                    const date = new Date(msg.createdAt).toDateString();
                    const showDate = date !== lastDate;
                    lastDate = date;
                    const isMe = msg.senderRole === 'pharmacy';
                    return (
                      <React.Fragment key={msg.id}>
                        {showDate && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '12px 0' }}>
                            <div style={{ flex: 1, height: '0.5px', backgroundColor: '#E5E7EB' }} />
                            <span style={{ fontSize: 12, color: '#888', backgroundColor: '#F7F8FA', padding: '2px 10px', borderRadius: 20 }}>
                              {formatDate(msg.createdAt)}
                            </span>
                            <div style={{ flex: 1, height: '0.5px', backgroundColor: '#E5E7EB' }} />
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', marginBottom: 8, gap: 8, alignItems: 'flex-end' }}>
                          {!isMe && (
                            <div style={{ width: 32, height: 32, borderRadius: 16, backgroundColor: '#E8F8F2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
                              👤
                            </div>
                          )}
                          <div style={{
                            maxWidth: '70%',
                            borderRadius: 16,
                            padding: '10px 14px',
                            backgroundColor: isMe ? '#1D9E75' : '#FFF',
                            border: isMe ? 'none' : '0.5px solid #E5E7EB',
                            borderBottomRightRadius: isMe ? 4 : 16,
                            borderBottomLeftRadius: isMe ? 16 : 4,
                            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                          }}>
                            {!isMe && (
                              <p style={{ fontSize: 11, color: '#1D9E75', fontWeight: 700, margin: '0 0 3px' }}>
                                {msg.senderName || 'Customer'}
                              </p>
                            )}
                            <p style={{ fontSize: 14, color: isMe ? '#FFF' : '#1A1A1A', margin: 0, lineHeight: 1.5 }}>
                              {msg.text}
                            </p>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4, marginTop: 4, alignItems: 'center' }}>
                              <span style={{ fontSize: 10, color: isMe ? 'rgba(255,255,255,0.7)' : '#888' }}>
                                {formatTime(msg.createdAt)}
                              </span>
                              {isMe && (
                                <span style={{ fontSize: 11, color: msg.readByCustomer ? '#A8EDCF' : 'rgba(255,255,255,0.5)' }}>
                                  {msg.readByCustomer ? '✓✓' : '✓'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  });
                })()}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* QUICK REPLIES */}
          <div style={{ overflowX: 'auto', padding: '8px 16px', borderTop: '0.5px solid #E5E7EB', backgroundColor: '#FFF', whiteSpace: 'nowrap' }}>
            {QUICK_REPLIES.map((qr, i) => (
              <button
                key={i}
                style={{ display: 'inline-block', marginRight: 8, padding: '6px 12px', borderRadius: 20, border: '1px solid #E5E7EB', backgroundColor: '#F7F8FA', cursor: 'pointer', fontSize: 12, color: '#1D9E75', fontWeight: 500, whiteSpace: 'nowrap' }}
                onClick={() => setInput(qr)}
              >
                {qr}
              </button>
            ))}
          </div>

          {/* INPUT BAR */}
          <div style={{ padding: '12px 16px', backgroundColor: '#FFF', borderTop: '1px solid #E5E7EB', display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <textarea
              style={{ flex: 1, padding: '10px 14px', borderRadius: 12, border: '1px solid #E5E7EB', fontSize: 14, resize: 'none', outline: 'none', backgroundColor: '#F7F8FA', fontFamily: 'inherit', maxHeight: 100, minHeight: 44 }}
              placeholder={`Reply to ${activeChat.customerName || 'customer'}...`}
              value={input}
              onChange={e => setInput(e.target.value)}
              rows={1}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
            />
            <button
              style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: input.trim() && !sending ? '#1D9E75' : '#A8D5C2', border: 'none', cursor: input.trim() && !sending ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, color: '#FFF', flexShrink: 0 }}
              onClick={sendMessage}
              disabled={!input.trim() || sending}
            >
              {sending ? '...' : '↑'}
            </button>
          </div>
        </div>
      ) : (
        /* NO CHAT SELECTED */
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, backgroundColor: '#F7F8FA' }}>
          <p style={{ fontSize: 64, margin: 0 }}>💬</p>
          <p style={{ fontSize: 18, fontWeight: 700, color: '#1A1A1A', margin: 0 }}>Select a conversation</p>
          <p style={{ fontSize: 14, color: '#888', margin: 0, textAlign: 'center', maxWidth: 300 }}>
            Choose a customer chat from the list on the left to start replying
          </p>
          {chats.length > 0 && (
            <div style={{ marginTop: 8, backgroundColor: '#E8F8F2', borderRadius: 10, padding: '10px 20px', border: '1px solid #B2DFD0' }}>
              <p style={{ fontSize: 13, color: '#1D6B52', margin: 0, fontWeight: 600 }}>
                {chats.length} conversation{chats.length > 1 ? 's' : ''} waiting
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}